import React, { useState, useEffect } from "react";
import { getData, postData, putData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaTruck,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaStore,
  FaShippingFast,
  FaBox,
  FaMotorcycle
} from "react-icons/fa";

function DeliveryTypesManager() {
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    delivery_description: '',
    active: true
  });

  useEffect(() => {
    loadDeliveryTypes();
  }, []);

  const loadDeliveryTypes = async () => {
    try {
      setLoading(true);
  
      const response = await getData('/api/admin/get_delivery_types');      
      // Inicializar como array vacío por defecto
      let typesData = [];
      
      if (response && response.data) {
        // Si response.data es directamente un array
        if (Array.isArray(response.data)) {
          typesData = response.data;
        }
        // Si está en response.data.deliveryTypes
        else if (response.data.deliveryTypes && Array.isArray(response.data.deliveryTypes)) {
          typesData = response.data.deliveryTypes;
        }
        // Si está en response.data.types
        else if (response.data.types && Array.isArray(response.data.types)) {
          typesData = response.data.types;
        }
        // Explorar otras propiedades posibles
        else {
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && typesData.length === 0) {
              typesData = response.data[key];
            }
          });
        }
      }
          
      // Asegurar que siempre sea un array
      if (!Array.isArray(typesData)) {
        typesData = [];
      }
      
      setDeliveryTypes(typesData);
    } catch (error) {
      console.error('Error cargando tipos de entrega:', error);
      toastError('Error al cargar los tipos de entrega');
      setDeliveryTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          delivery_description: formData.delivery_description.trim().toUpperCase(),
          active: formData.active ? 1 : 0,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingType) {
        dataToSend.id = editingType.id;
        await putData('/api/admin/update_delivery_type', dataToSend);
        toastSuccess(`Tipo "${formData.delivery_description}" actualizado exitosamente`);
      } else {
        await postData('/api/admin/create_delivery_type', dataToSend);
        toastSuccess(`Tipo "${formData.delivery_description}" creado exitosamente`);
      }
      
      setShowModal(false);
      resetForm();
      await loadDeliveryTypes();
    } catch (error) {
      console.error('Error guardando tipo de entrega:', error);
      toastError(editingType ? 'Error al actualizar el tipo' : 'Error al crear el tipo');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingType(null);
    setFormData({
      delivery_description: '',
      active: true
    });
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      delivery_description: type.delivery_description || '',
      active: type.active !== undefined ? Boolean(type.active) : true
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingType(null);
    setFormData({
      delivery_description: '',
      active: true
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (type) => {
    setDeletingType(type);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingType) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo (soft delete)
      const dataToSend = {
        data: {
          id: deletingType.id,
          delivery_description: deletingType.delivery_description,
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_delivery_type', dataToSend);
      toastSuccess(`Tipo "${deletingType.delivery_description}" eliminado exitosamente`);
      setShowDeleteModal(false);
      setDeletingType(null);
      await loadDeliveryTypes();
    } catch (error) {
      console.error('Error eliminando tipo:', error);
      toastError('Error al eliminar el tipo');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingType(null);
  };

  // Tipos de entrega basados en la imagen proporcionada
  const deliveryExamples = [
    'CREDIFIN SUC',
    'MANDA COM',
    'RETIRA CLIENTE',
    'REPARTO',
    'VIA CARGO DOM',
    'VIA CARGO SUC',
    'CAMIONERA',
    'MARAVILLA'
  ];

  // Función para obtener el ícono según el tipo de entrega
  const getDeliveryIcon = (description) => {
    const desc = description.toLowerCase();
    if (desc.includes('retira') || desc.includes('retiro')) {
      return <FaStore className="me-2 text-primary" />;
    } else if (desc.includes('reparto') || desc.includes('domicilio')) {
      return <FaShippingFast className="me-2 text-success" />;
    } else if (desc.includes('cargo') || desc.includes('camionera')) {
      return <FaTruck className="me-2 text-warning" />;
    } else if (desc.includes('moto') || desc.includes('express')) {
      return <FaMotorcycle className="me-2 text-info" />;
    } else {
      return <FaBox className="me-2 text-secondary" />;
    }
  };

  // Debug: verificar el estado antes del render

  // Filtrar tipos de entrega según el toggle
  const filteredDeliveryTypes = showInactive 
    ? deliveryTypes 
    : deliveryTypes.filter(type => type.active);

  return (
    <div className="delivery-types-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaTruck className="me-2 text-primary" />
            Gestión de Tipos de Entrega
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nuevo Tipo
            </button>
          </div>
        </div>

        <div className="card-body">
          {!showInactive && deliveryTypes.length > filteredDeliveryTypes.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{deliveryTypes.length - filteredDeliveryTypes.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando tipos de entrega...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo de Entrega</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Actualizado por</th>
                    <th>Última Actualización</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(filteredDeliveryTypes) || filteredDeliveryTypes.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        {!Array.isArray(deliveryTypes) ? 
                          'Error cargando tipos de entrega' : 
                          showInactive ? 'No hay tipos de entrega registrados' : 'No hay tipos activos. Activa el toggle "Mostrar inactivos" para ver tipos eliminados.'
                        }
                      </td>
                    </tr>
                  ) : (
                    filteredDeliveryTypes.map(type => (
                      <tr key={type.id}>
                        <td>#{type.id}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            {getDeliveryIcon(type.delivery_description)}
                            <strong className="text-uppercase">{type.delivery_description}</strong>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${type.active ? 'bg-success' : 'bg-danger'}`}>
                            {type.active ? (
                              <>
                                <FaCheck className="me-1" />
                                Activo
                              </>
                            ) : (
                              <>
                                <FaTimes className="me-1" />
                                Inactivo
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {type.inserted_date ? new Date(type.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {type.updated_by ? `Usuario #${type.updated_by}` : 'Sin actualizar'}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {type.updated_at ? new Date(type.updated_at).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(type)}
                            disabled={loading}
                            title="Editar tipo"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(type)}
                            disabled={loading || !type.active}
                            title={type.active ? "Eliminar tipo" : "Tipo ya inactivo"}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar tipo */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingType ? 'Editar Tipo de Entrega' : 'Nuevo Tipo de Entrega'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="mb-3">
                        <label className="form-label">Descripción del Tipo de Entrega *</label>
                        <input
                          type="text"
                          className="form-control text-uppercase"
                          name="delivery_description"
                          value={formData.delivery_description}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          placeholder="Ej: RETIRA CLIENTE, REPARTO, etc."
                          style={{ textTransform: 'uppercase' }}
                        />
                        <div className="form-text">
                          Se convertirá automáticamente a mayúsculas
                        </div>
                      </div>

                      {/* Botones de ejemplos rápidos */}
                      <div className="mb-3">
                        <label className="form-label">Tipos comunes:</label>
                        <div className="d-flex flex-wrap gap-1">
                          {deliveryExamples.map(example => (
                            <button
                              key={example}
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setFormData(prev => ({ ...prev, delivery_description: example }))}
                              disabled={loading}
                            >
                              {getDeliveryIcon(example)}
                              {example}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            <FaCheck className="me-2 text-success" />
                            Tipo activo
                          </label>
                          <div className="form-text">
                            Los tipos inactivos no aparecerán disponibles para nuevos pedidos
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-info">
                    <FaTruck className="me-2" />
                    <strong>Nota:</strong> Los tipos de entrega definen cómo se realizará la distribución del pedido al cliente final.
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <FaStore className="display-6 text-primary mb-2" />
                          <h6>Retiro en Local</h6>
                          <small className="text-muted">Cliente retira en tienda</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-success">
                        <div className="card-body text-center">
                          <FaShippingFast className="display-6 text-success mb-2" />
                          <h6>Envío a Domicilio</h6>
                          <small className="text-muted">Entrega en domicilio del cliente</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.delivery_description.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingType ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el tipo <strong>"{deletingType?.delivery_description}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El tipo será marcado como inactivo y no aparecerá disponible para nuevos pedidos.
                </div>
                <div className="alert alert-info">
                  <FaTruck className="me-2" />
                  Los pedidos existentes que usen este tipo de entrega no se verán afectados.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryTypesManager;
